import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import API from '@/lib/api';
import { getPostalCode, POSTAL_CODE_EVENT, openPostalCodeGate } from '@/lib/postalCode';

const HIDDEN_PREFIXES = ['/admin', '/vendeur', '/traiteur', '/livreur', '/connexion', '/mot-de-passe-oublie', '/reinitialiser-mot-de-passe'];
const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

function remaining(iso) {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3600000);
  const d = Math.floor(h / 24);
  if (d >= 1) return `${d} j ${String(h - d * 24).padStart(2, '0')} h`;
  if (h >= 1) return `${h} h ${String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0')}`;
  return `${Math.max(1, Math.floor(ms / 60000))} min`;
}

/** Bandeau visiteur, sur toutes les pages publiques :
 *  « Passez votre commande avant samedi 26 sept. à 22h pour être livré le mardi 29 sept. (8h - 20h) · livraison suivante : mardi 6 oct. » */
export default function DeliveryRhythmBanner() {
  const { pathname } = useLocation();
  const [pc, setPc] = useState(getPostalCode());
  const [data, setData] = useState(null);
  const [, tick] = useState(0);

  const load = useCallback((code) => {
    API.get('/delivery-zones/next', { params: code ? { postal_code: code } : {} }).then(r => setData(r.data)).catch(() => setData(null));
  }, []);

  useEffect(() => { load(pc); }, [pc, load]);
  useEffect(() => {
    const onPc = (e) => setPc(e.detail || getPostalCode());
    window.addEventListener(POSTAL_CODE_EVENT, onPc);
    const t = setInterval(() => tick(x => x + 1), 60000);
    const refresh = setInterval(() => load(getPostalCode()), 10 * 60000);
    return () => { window.removeEventListener(POSTAL_CODE_EVENT, onPc); clearInterval(t); clearInterval(refresh); };
  }, [load]);

  if (HIDDEN_PREFIXES.some(p => pathname.startsWith(p)) || !data?.next) return null;
  const { next, following, zone, found } = data;
  const left = remaining(next.cutoff_at);
  if (!left) { load(pc); return null; }
  const urgent = new Date(next.cutoff_at) - Date.now() < 12 * 3600000;

  return (
    <div className={`w-full border-b font-sans text-[13px] leading-snug ${urgent ? 'bg-brick text-paper border-brick' : 'bg-stone-text text-paper border-stone-text'}`} data-testid="delivery-rhythm-banner">
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-2 flex flex-wrap items-center justify-center md:justify-between gap-x-4 gap-y-1 text-center md:text-left">
        <p className="flex flex-wrap items-center justify-center gap-x-1.5" data-testid="delivery-rhythm-banner-text">
          <CalendarClock className="h-4 w-4 text-waffle shrink-0" />
          <span>Passez votre commande avant le <strong data-testid="banner-cutoff">{next.cutoff_label}</strong></span>
          <span className="opacity-70">(il reste {left})</span>
          <span>pour être livré le <strong data-testid="banner-delivery">{next.delivery_label}</strong> <span className="opacity-80">({next.delivery_window})</span></span>
          {following && <span className="opacity-80" data-testid="banner-following">· livraison suivante : {following.delivery_label}</span>}
        </p>
        <button type="button" onClick={openPostalCodeGate} className="text-xs underline underline-offset-2 opacity-80 hover:opacity-100 transition-opacity duration-200" data-testid="banner-postal-code">
          {found ? `${cap(zone?.name || '')} · ${pc}` : (next.zones ? `Pétales ${next.zones.join(', ')} — indiquez votre code postal` : 'Indiquez votre code postal')}
        </button>
      </div>
    </div>
  );
}
